/**
 * Publisher Types
 *
 * Types for the Publisher entity - a domain-level record that consolidates
 * authority, media type, AI policy, and branding data per domain.
 */

import type {
	CitationAuthorityType,
	CitationClassificationMethod,
	CitationMediaType,
	PublisherAiPolicyStatus,
} from "@repo/database";

export type {
	CitationAuthorityType,
	CitationMediaType,
	CitationClassificationMethod,
	PublisherAiPolicyStatus,
};

export const AI_POLICY_STATUSES = ["green", "yellow", "red"] as const;

// =============================================================================
// AI ACCESS CHECK TYPES
// =============================================================================

export interface BlockedBotInfo {
	name: string;
	company?: string;
	slug: string;
}

/**
 * Which AI role a blocked bot plays. Stored verbatim in
 * `PublisherBlockedBot.category`.
 *
 * - `ai_visitor`: live fetchers (ChatGPT-User). A block is red.
 * - `ai_search_indexer`: an assistant's own search indexer (OAI-SearchBot,
 *   PerplexityBot, Claude-SearchBot). A block is yellow and informational:
 *   it never changes AIPVS.
 * - `ai_model_trainer`: training crawlers (GPTBot). A block is yellow.
 */
export type PublisherBlockedBotCategory =
	| "ai_visitor"
	| "ai_search_indexer"
	| "ai_model_trainer";

export const PUBLISHER_BLOCKED_BOT_CATEGORIES = [
	"ai_visitor",
	"ai_search_indexer",
	"ai_model_trainer",
] as const satisfies readonly PublisherBlockedBotCategory[];

/**
 * What a robots.txt fetch found.
 *
 * - `found`: robots.txt was read and analyzed.
 * - `missing`: the server answered with no robots.txt (404 and similar).
 *   Every crawler is allowed, so the status is green.
 * - `unreachable`: robots.txt couldn't be loaded (network error, timeout,
 *   5xx, 401/403/429). No verdict is possible: show "Couldn't check".
 * - `cloudflare_blocked`: Cloudflare bot protection challenged the request.
 */
export type RobotsTxtState =
	| "found"
	| "missing"
	| "unreachable"
	| "cloudflare_blocked";

export interface AiAccessCheckResult {
	domain: string;
	analysis: {
		/**
		 * Kept to three values for API compatibility. When `robotsTxtState` is
		 * `unreachable` this is "green" but carries no verdict; check
		 * `robotsTxtState` first.
		 */
		status: "red" | "yellow" | "green";
		isSpyglassesAllowed: boolean;
		hasWildcardDisallow: boolean;
		blockedBots: {
			aiVisitors: BlockedBotInfo[];
			/** Informational: never makes the status red. */
			aiSearchIndexers: BlockedBotInfo[];
			aiModelTrainers: BlockedBotInfo[];
		};
		summary: string;
	};
	robotsTxtFound: boolean;
	/**
	 * Finer-grained than `robotsTxtFound`. Cached results always say `found`:
	 * the Publisher record doesn't keep whether the file existed.
	 */
	robotsTxtState: RobotsTxtState;
	robotsTxtUrl: string;
	cloudflareBlocked?: boolean;
	cloudflareProtectionType?: string;
	/** Whether the result came from a cached Publisher record. */
	cached: boolean;
}

// =============================================================================
// AI POLICY REASONS + SEARCH INDEXER FLAGS (pure, client-safe)
// =============================================================================

/**
 * Which kinds of AI bot a publisher blocks, derived from its
 * `PublisherBlockedBot` categories. Explains a yellow policy dot: trainers,
 * search indexers, or both. `visitors` is the red case.
 */
export interface AiPolicyReasons {
	visitors: boolean;
	indexers: boolean;
	trainers: boolean;
}

/** A blocked AI search indexer, with the assistant it feeds. */
export interface AiIndexerFlag {
	botTypeId: string;
	/** BotType name as stored, e.g. "OpenAI SearchBot". */
	botName: string;
	company: string | null;
	/** Assistant display name, e.g. "ChatGPT". See {@link resolveAiPlatformLabel}. */
	platform: string;
}

/** The AI-policy detail every publisher read surface exposes. */
export interface PublisherAiPolicyDetail {
	aiPolicyReasons: AiPolicyReasons;
	aiIndexerFlags: AiIndexerFlag[];
}

/**
 * Prisma `select` for `Publisher.blockedBotTypes` that carries everything
 * {@link derivePublisherAiPolicyDetail} needs. A plain object, so it is safe
 * in client bundles.
 */
export const AI_POLICY_BLOCKED_BOT_SELECT = {
	category: true,
	botTypeId: true,
	botType: {
		select: {
			name: true,
			company: true,
			aiReferrer: { select: { name: true } },
		},
	},
} as const;

export interface AiPolicyBlockedBotRow {
	category: string;
	botTypeId: string;
	botType: {
		name: string;
		company: string | null;
		aiReferrer?: { name: string } | null;
	};
}

const PLATFORM_BY_REFERRER: Record<string, string> = {
	chatgpt: "ChatGPT",
	openai: "ChatGPT",
	claude: "Claude",
	gemini: "Gemini",
	perplexity: "Perplexity",
	copilot: "Copilot",
	"google ai overview": "Google AI Overviews",
	"google ai overviews": "Google AI Overviews",
};

/** Company substring → assistant. Checked in order. */
const PLATFORM_BY_COMPANY: Array<[string, string]> = [
	["openai", "ChatGPT"],
	["anthropic", "Claude"],
	["perplexity", "Perplexity"],
	["google", "Gemini"],
	["microsoft", "Copilot"],
	["meta", "Meta AI"],
	["mistral", "Le Chat"],
	["deepseek", "DeepSeek"],
	["xai", "Grok"],
	["x.ai", "Grok"],
	["duckduckgo", "DuckDuckGo"],
	["you.com", "You.com"],
];

/**
 * A stable assistant label for a bot type: "ChatGPT" for ChatGPT-User,
 * OAI-SearchBot and GPTBot alike, so a live fetcher and its indexer line up.
 *
 * Order: the linked AiReferrer's name (canonicalized), then the bot's
 * company, then the raw referrer name, company, and finally the bot name.
 */
export function resolveAiPlatformLabel(input: {
	botName: string;
	company?: string | null;
	aiReferrerName?: string | null;
}): string {
	const referrer = input.aiReferrerName?.trim();
	if (referrer) {
		const canonical = PLATFORM_BY_REFERRER[referrer.toLowerCase()];
		if (canonical) return canonical;
	}
	if (/ai overview/i.test(input.botName)) return "Google AI Overviews";
	const company = input.company?.trim();
	if (company) {
		const lower = company.toLowerCase();
		for (const [needle, label] of PLATFORM_BY_COMPANY) {
			if (lower.includes(needle)) return label;
		}
	}
	return referrer || company || input.botName;
}

/** Derive yellow/red reasons from blocked-bot categories. */
export function deriveAiPolicyReasons(
	categories: Iterable<string>,
): AiPolicyReasons {
	const reasons: AiPolicyReasons = {
		visitors: false,
		indexers: false,
		trainers: false,
	};
	for (const category of categories) {
		if (category === "ai_visitor") reasons.visitors = true;
		else if (category === "ai_search_indexer") reasons.indexers = true;
		else if (category === "ai_model_trainer") reasons.trainers = true;
	}
	return reasons;
}

/** Blocked indexer rows → flags, sorted by platform then bot name. */
export function buildAiIndexerFlags(
	rows: readonly AiPolicyBlockedBotRow[],
): AiIndexerFlag[] {
	return rows
		.filter((row) => row.category === "ai_search_indexer")
		.map((row) => ({
			botTypeId: row.botTypeId,
			botName: row.botType.name,
			company: row.botType.company,
			platform: resolveAiPlatformLabel({
				botName: row.botType.name,
				company: row.botType.company,
				aiReferrerName: row.botType.aiReferrer?.name,
			}),
		}))
		.sort(
			(a, b) =>
				a.platform.localeCompare(b.platform) ||
				a.botName.localeCompare(b.botName),
		);
}

/** Reasons + indexer flags from `blockedBotTypes` rows. */
export function derivePublisherAiPolicyDetail(
	rows: readonly AiPolicyBlockedBotRow[] | null | undefined,
): PublisherAiPolicyDetail {
	const list = rows ?? [];
	return {
		aiPolicyReasons: deriveAiPolicyReasons(list.map((row) => row.category)),
		aiIndexerFlags: buildAiIndexerFlags(list),
	};
}

/**
 * Swap a publisher's raw `blockedBotTypes` rows for the derived
 * {@link PublisherAiPolicyDetail}. Use on any read that selected
 * `blockedBotTypes: { select: AI_POLICY_BLOCKED_BOT_SELECT }`.
 */
export function withPublisherAiPolicyDetail<
	T extends { blockedBotTypes: readonly AiPolicyBlockedBotRow[] },
>(publisher: T): Omit<T, "blockedBotTypes"> & PublisherAiPolicyDetail {
	const { blockedBotTypes, ...rest } = publisher;
	return { ...rest, ...derivePublisherAiPolicyDetail(blockedBotTypes) };
}

// =============================================================================
// PLATFORM CONFIGURATION
// =============================================================================

/**
 * Defines how to extract a sub-publisher identifier from a platform URL.
 *
 * Three extraction strategies:
 *  - `subdomain`: sub-publisher is a subdomain (e.g., platformer.substack.com)
 *  - `path`: sub-publisher is identified by a path prefix (e.g., reddit.com/r/programming)
 *  - `none`: the platform IS the publisher; no sub-publisher extraction
 *
 * For `path` strategy, `pathPrefixes` lists the first-segment values that
 * indicate a sub-publisher. Segments not in this list are ignored (e.g.,
 * youtube.com/watch is not a channel, so it falls back to youtube.com).
 */
export interface PlatformConfig {
	domain: string;
	strategy: "subdomain" | "path" | "none";
	pathPrefixes?: string[];
	/** Path segments to explicitly skip (utility pages, not sub-publishers) */
	ignoredPaths?: string[];
}

export const PLATFORM_CONFIGS: PlatformConfig[] = [
	// --- Newsletter / Blogging Platforms (subdomain-based) ---
	{ domain: "substack.com", strategy: "subdomain" },
	{ domain: "beehiiv.com", strategy: "subdomain" },
	{ domain: "ghost.io", strategy: "subdomain" },
	{ domain: "hashnode.dev", strategy: "subdomain" },
	{ domain: "wordpress.com", strategy: "subdomain" },
	{ domain: "blogspot.com", strategy: "subdomain" },
	{ domain: "notion.so", strategy: "subdomain" },
	{ domain: "gitbook.io", strategy: "subdomain" },

	// --- Video Platforms (path-based, channel in URL when available) ---
	{
		domain: "youtube.com",
		strategy: "path",
		pathPrefixes: ["@", "c", "channel"],
	},
	{ domain: "vimeo.com", strategy: "path", pathPrefixes: ["channels"] },
	{
		domain: "twitch.tv",
		strategy: "path",
		pathPrefixes: [],
		ignoredPaths: ["directory", "downloads", "jobs", "p", "settings"],
	},
	{ domain: "dailymotion.com", strategy: "path", pathPrefixes: [] },
	{ domain: "rumble.com", strategy: "path", pathPrefixes: ["c"] },

	// --- Podcast / Audio Platforms (path-based) ---
	{
		domain: "podcasts.apple.com",
		strategy: "path",
		pathPrefixes: ["podcast"],
	},
	{ domain: "open.spotify.com", strategy: "path", pathPrefixes: ["show"] },
	{ domain: "anchor.fm", strategy: "path", pathPrefixes: ["s"] },
	{ domain: "transistor.fm", strategy: "none" },

	// --- Community / Forum Platforms (path-based) ---
	{ domain: "reddit.com", strategy: "path", pathPrefixes: ["r"] },
	{
		domain: "quora.com",
		strategy: "path",
		pathPrefixes: ["profile"],
		ignoredPaths: ["topic", "search", "answer"],
	},
	{ domain: "stackoverflow.com", strategy: "none" },
	{ domain: "stackexchange.com", strategy: "subdomain" },
	{ domain: "news.ycombinator.com", strategy: "none" },
	{
		domain: "producthunt.com",
		strategy: "path",
		pathPrefixes: ["products", "posts"],
	},
	{
		domain: "discord.com",
		strategy: "path",
		pathPrefixes: ["invite", "channels"],
	},

	// --- Long-form / Knowledge Platforms ---
	{
		domain: "medium.com",
		strategy: "path",
		pathPrefixes: [],
		ignoredPaths: ["tag", "search", "topics", "me", "plans"],
	},
	{ domain: "dev.to", strategy: "path", pathPrefixes: [] },
	{ domain: "tumblr.com", strategy: "subdomain" },
	{
		domain: "linkedin.com",
		strategy: "path",
		pathPrefixes: ["company", "in", "pulse"],
	},
	{ domain: "coda.io", strategy: "none" },
	{ domain: "docs.google.com", strategy: "none" },
	{ domain: "wikia.com", strategy: "subdomain" },
	{ domain: "fandom.com", strategy: "subdomain" },
	{ domain: "wikipedia.org", strategy: "subdomain" },

	// --- Developer / Technical Writing ---
	{ domain: "hackernoon.com", strategy: "none" },
	{ domain: "codepen.io", strategy: "path", pathPrefixes: [] },
	{ domain: "dzone.com", strategy: "none" },
	{ domain: "infoq.com", strategy: "none" },
	{ domain: "freecodecamp.org", strategy: "none" },

	// --- Slide / Presentation ---
	{ domain: "slideshare.net", strategy: "path", pathPrefixes: [] },
	{ domain: "speakerdeck.com", strategy: "path", pathPrefixes: [] },

	// --- Academic / Research ---
	{ domain: "academia.edu", strategy: "none" },
	{ domain: "ssrn.com", strategy: "none" },
	{ domain: "researchgate.net", strategy: "path", pathPrefixes: ["profile"] },
	{ domain: "arxiv.org", strategy: "none" },
	{ domain: "zenodo.org", strategy: "none" },

	// --- Creator Economy ---
	{ domain: "patreon.com", strategy: "path", pathPrefixes: [] },
	{ domain: "ko-fi.com", strategy: "path", pathPrefixes: [] },
	{ domain: "gumroad.com", strategy: "path", pathPrefixes: [] },
	{ domain: "loom.com", strategy: "path", pathPrefixes: ["share"] },
];

/**
 * Simple flat list of all platform domains, derived from configs.
 * Used for quick "is this a platform?" checks.
 */
export const PLATFORM_DOMAINS: string[] = PLATFORM_CONFIGS.map((c) => c.domain);

export interface PublisherClassificationInput {
	domain: string;
	sampleUrl?: string;
	sampleTitle?: string;
	sampleDescription?: string;
}

export interface PublisherClassificationResult {
	authorityType: {
		value: CitationAuthorityType;
		confidence: number;
	};
	mediaType: {
		value: CitationMediaType;
		confidence: number;
	};
	classificationMethod: "heuristic" | "llm";
}

export interface PublisherAiPolicyResult {
	status: "green" | "yellow" | "red";
	hasWildcardDisallow: boolean;
	isSpyglassesAllowed: boolean;
	blockedBots: Array<{
		botTypeId: string;
		category: PublisherBlockedBotCategory;
	}>;
}

export interface PublisherCreateInput {
	domain: string;
	name?: string;
	logoUrl?: string;
	authorityType?: string;
	mediaType?: string;
	isPlatform?: boolean;
	platformId?: string;
	classificationMethod?: "heuristic" | "llm";
}

export interface PublisherWithRelations {
	id: string;
	domain: string;
	name: string | null;
	logoUrl: string | null;
	authorityType: string | null;
	mediaType: string | null;
	isPlatform: boolean;
	platformId: string | null;
	aiPolicyStatus: string | null;
	aiPolicyCheckedAt: Date | null;
	hasWildcardDisallow: boolean | null;
	isSpyglassesAllowed: boolean | null;
	classificationMethod: string | null;
	classifiedAt: Date | null;
	createdAt: Date;
	updatedAt: Date;
	blockedBotTypes: Array<{
		id: string;
		botTypeId: string;
		category: string;
	}>;
}

// =============================================================================
// PLATFORM LOOKUP HELPERS
// =============================================================================

/**
 * Find the PlatformConfig for a given hostname.
 * Handles exact match and subdomain match (e.g., "en.wikipedia.org" -> wikipedia.org config).
 */
function findPlatformConfig(hostname: string): PlatformConfig | null {
	for (const config of PLATFORM_CONFIGS) {
		if (hostname === config.domain || hostname === `www.${config.domain}`) {
			return config;
		}
		if (hostname.endsWith(`.${config.domain}`)) {
			return config;
		}
	}
	return null;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Extracts the publisher identifier from a URL.
 *
 * For non-platform URLs, returns the root domain (e.g., "forbes.com").
 * For platform URLs, returns a domain-based identifier that captures the
 * sub-publisher when extractable:
 *   - subdomain strategy: "platformer.substack.com"
 *   - path strategy:      "reddit.com/r/programming", "youtube.com/@mkbhd"
 *   - none strategy:      "stackoverflow.com" (platform = publisher)
 *
 * When the URL doesn't contain enough info to identify a sub-publisher
 * (e.g., youtube.com/watch?v=...), falls back to the platform root domain.
 */
export function extractPublisherDomain(url: string): string {
	try {
		const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
		const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, "");

		const config = findPlatformConfig(hostname);
		if (!config) {
			return hostname;
		}

		// Strategy: subdomain
		if (config.strategy === "subdomain") {
			// If the hostname IS the platform domain, no sub-publisher in the URL
			if (hostname === config.domain) {
				return hostname;
			}
			// Otherwise the full subdomain hostname is the publisher identifier
			return hostname;
		}

		// Strategy: none -- the platform itself is the publisher
		if (config.strategy === "none") {
			return config.domain;
		}

		// Strategy: path
		// Lowercase path segments to avoid case-sensitive duplicates
		// (e.g., reddit.com/r/EmailMarketing vs reddit.com/r/emailmarketing)
		const pathSegments = urlObj.pathname
			.toLowerCase()
			.split("/")
			.filter((s) => s.length > 0);
		if (pathSegments.length === 0) {
			return hostname;
		}

		const firstSegment = pathSegments[0];

		// Skip explicitly ignored paths (utility/navigation pages)
		if (config.ignoredPaths?.includes(firstSegment)) {
			return hostname;
		}

		// If pathPrefixes is defined and non-empty, the first segment must match
		if (config.pathPrefixes && config.pathPrefixes.length > 0) {
			// Handle @ as a prefix match (e.g., "@mkbhd" matches "@")
			const matchesPrefix = config.pathPrefixes.some((prefix) =>
				prefix === ""
					? true
					: prefix.startsWith("@")
						? firstSegment.startsWith("@")
						: firstSegment === prefix,
			);

			if (!matchesPrefix) {
				// URL doesn't contain a recognizable sub-publisher path
				return hostname;
			}

			// For prefixes like "r", "c", "channel", "show" the sub-publisher
			// is the NEXT segment: reddit.com/r/programming, youtube.com/c/mkbhd
			if (firstSegment.startsWith("@")) {
				return `${hostname}/${firstSegment}`;
			}
			if (pathSegments.length >= 2) {
				return `${hostname}/${firstSegment}/${pathSegments[1]}`;
			}
			return hostname;
		}

		// Empty pathPrefixes means the first segment IS the sub-publisher directly
		// (e.g., medium.com/netflix-techblog, dev.to/username, patreon.com/creator)
		return `${hostname}/${firstSegment}`;
	} catch {
		return url.toLowerCase().replace(/^www\./, "");
	}
}

/**
 * Checks whether a domain belongs to a known publisher platform.
 */
export function isPlatformDomain(domain: string): boolean {
	const normalized = domain.toLowerCase().replace(/^www\./, "");
	return findPlatformConfig(normalized) !== null;
}

/**
 * Extracts the platform root domain from a sub-publisher domain.
 * e.g., "youtube.com/@mkbhd" -> "youtube.com"
 * e.g., "platformer.substack.com" -> "substack.com"
 * e.g., "reddit.com/r/programming" -> "reddit.com"
 */
export function extractPlatformDomain(domain: string): string | null {
	const normalized = domain.toLowerCase().replace(/^www\./, "");

	for (const config of PLATFORM_CONFIGS) {
		if (normalized === config.domain) return config.domain;
		if (normalized.startsWith(`${config.domain}/`)) return config.domain;
		if (normalized.endsWith(`.${config.domain}`)) return config.domain;
	}

	return null;
}

/**
 * Derives a sub-platform kind from a publisher domain string. Pure function
 * over the existing PLATFORM_CONFIGS mapping — recomputed on demand rather
 * than persisted as a column, since the domain string already encodes it.
 *
 * Returns labels like:
 *   - "subreddit"          for reddit.com/r/...
 *   - "linkedin_company"   for linkedin.com/company/...
 *   - "linkedin_personal"  for linkedin.com/in/...
 *   - "linkedin_newsletter" for linkedin.com/pulse/...
 *   - "youtube_channel"    for youtube.com/@..., /c/..., /channel/...
 *   - "substack_newsletter" for *.substack.com
 *
 * Returns null for non-platform domains and for platform roots themselves
 * (e.g., "reddit.com" with no sub-publisher segment).
 */
export function derivePublisherKind(domain: string): string | null {
	if (!domain) return null;
	const normalized = domain.toLowerCase().replace(/^www\./, "");
	const platform = extractPlatformDomain(normalized);
	if (!platform) return null;
	if (normalized === platform) return null;

	// Subdomain-strategy platforms: any subdomain implies the canonical sub-kind
	const subdomainKinds: Record<string, string> = {
		"substack.com": "substack_newsletter",
		"beehiiv.com": "beehiiv_newsletter",
		"ghost.io": "ghost_blog",
		"hashnode.dev": "hashnode_blog",
		"wordpress.com": "wordpress_blog",
		"blogspot.com": "blogspot_blog",
		"notion.so": "notion_page",
		"gitbook.io": "gitbook_book",
		"tumblr.com": "tumblr_blog",
		"stackexchange.com": "stackexchange_site",
		"wikia.com": "wikia_wiki",
		"fandom.com": "fandom_wiki",
		"wikipedia.org": "wikipedia_edition",
	};
	if (normalized.endsWith(`.${platform}`) && subdomainKinds[platform]) {
		return subdomainKinds[platform];
	}

	// Path-strategy platforms: split path segments off the platform root
	const pathPart = normalized.startsWith(`${platform}/`)
		? normalized.slice(platform.length + 1)
		: "";
	const segments = pathPart.split("/").filter(Boolean);
	const first = segments[0] ?? "";
	if (!first) return null;

	switch (platform) {
		case "linkedin.com":
			if (first === "company") return "linkedin_company";
			if (first === "in") return "linkedin_personal";
			if (first === "pulse") return "linkedin_newsletter";
			return null;
		case "reddit.com":
			return first === "r" ? "subreddit" : null;
		case "youtube.com":
			if (first.startsWith("@") || first === "c" || first === "channel")
				return "youtube_channel";
			return null;
		case "vimeo.com":
			return first === "channels" ? "vimeo_channel" : null;
		case "twitch.tv":
			return "twitch_channel";
		case "dailymotion.com":
			return "dailymotion_channel";
		case "rumble.com":
			return first === "c" ? "rumble_channel" : null;
		case "podcasts.apple.com":
			return first === "podcast" ? "apple_podcast" : null;
		case "open.spotify.com":
			return first === "show" ? "spotify_show" : null;
		case "anchor.fm":
			return first === "s" ? "anchor_show" : null;
		case "quora.com":
			return first === "profile" ? "quora_profile" : null;
		case "producthunt.com":
			if (first === "products") return "producthunt_product";
			if (first === "posts") return "producthunt_post";
			return null;
		case "discord.com":
			if (first === "invite") return "discord_invite";
			if (first === "channels") return "discord_server";
			return null;
		case "medium.com":
			return "medium_publication";
		case "dev.to":
			return "devto_profile";
		case "codepen.io":
			return "codepen_profile";
		case "slideshare.net":
			return "slideshare_profile";
		case "speakerdeck.com":
			return "speakerdeck_profile";
		case "researchgate.net":
			return first === "profile" ? "researchgate_profile" : null;
		case "patreon.com":
			return "patreon_creator";
		case "ko-fi.com":
			return "kofi_page";
		case "gumroad.com":
			return "gumroad_seller";
		case "loom.com":
			return first === "share" ? "loom_share" : null;
		default:
			return null;
	}
}
